// The one message component (spec §3.7): every error is a big red box in the middle of the
// screen with one button, "OK, I'll fix it"; a double-check is the orange "Are you sure?" with
// "Go back" / "… anyway". Success is a small toast at the bottom (`notify`), never this.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { OctagonXIcon, TriangleAlertIcon } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  Button,
  toast,
} from "@axny/platform-ui";
import { cn } from "@/lib/utils";
import { useDialogDrag } from "./dialog-drag";

interface Message {
  kind: "error" | "warning";
  title: string;
  body: ReactNode;
  lines?: readonly string[];
  /** Warning only: the "… anyway" button. */
  confirmLabel?: string;
  resolve: (ok: boolean) => void;
}

interface MessageApi {
  /** The big red message. Resolves when the person clicks "OK, I'll fix it". */
  error(title: string, body: ReactNode, lines?: readonly string[]): Promise<void>;
  /** The orange "Are you sure?". Resolves true on "… anyway". */
  confirm(
    title: string,
    body: ReactNode,
    opts?: { lines?: readonly string[]; confirmLabel?: string },
  ): Promise<boolean>;
}

const MessageContext = createContext<MessageApi | null>(null);

export function useMessage(): MessageApi {
  const api = useContext(MessageContext);
  if (!api) throw new Error("useMessage outside MessageProvider");
  return api;
}

/** A small grey message at the bottom: saved, approved, undone. `timeout`: shorter where it would sit on a form. */
export function notify(title: string, description?: string, timeout = 2600) {
  toast.add({ title, description, timeout });
}

/** A toast with an Undo button, kept a while so there is time to press it; Undo closes it. */
export function notifyUndo(title: string, onUndo: () => void, timeout = 8000) {
  const id: string = toast.add({
    title,
    timeout,
    actionProps: {
      children: "Undo",
      onClick: () => {
        toast.close(id);
        onUndo();
      },
    },
  });
}

/** The Shipping page's toasts: gone before they get in the way of Add a line, under them. */
export const SHIPPING_TOAST_MS = 1600;

export function MessageProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<Message | null>(null);
  const queue = useRef<Message[]>([]);
  // The one button Enter presses: "OK, I'll fix it", or "Go back" on a warning (the safe answer).
  const answer = useRef<HTMLButtonElement>(null);
  // Moved by its coloured bar; each message opens in the middle again.
  const drag = useDialogDrag();
  const resetDrag = drag.reset;
  useEffect(() => {
    if (message) resetDrag();
  }, [message, resetDrag]);

  // Opened from a key press in the grid, the dialog does not take focus by itself: give it to the
  // answer, so Enter answers the message instead of reaching the grid behind it.
  useEffect(() => {
    if (!message) return;
    const id = setTimeout(() => answer.current?.focus(), 30);
    return () => clearTimeout(id);
  }, [message]);

  const show = useCallback((m: Message) => {
    setMessage((current) => {
      if (current) {
        // The same message again while it shows: answer it with the one on screen. Its lines too —
        // two "Are you sure?" with one title and body but other lines are two questions.
        const same = (x: Message) =>
          x.kind === m.kind &&
          x.title === m.title &&
          x.body === m.body &&
          (x.lines ?? []).join("\n") === (m.lines ?? []).join("\n");
        if (same(current) || queue.current.some(same)) m.resolve(false);
        else queue.current.push(m);
        return current;
      }
      return m;
    });
  }, []);

  const close = (ok: boolean) => {
    message?.resolve(ok);
    setMessage(queue.current.shift() ?? null);
  };

  const api = useMemo<MessageApi>(
    () => ({
      error: (title, body, lines) =>
        new Promise<void>((resolve) => show({ kind: "error", title, body, lines, resolve: () => resolve() })),
      confirm: (title, body, opts) =>
        new Promise<boolean>((resolve) =>
          show({ kind: "warning", title, body, lines: opts?.lines, confirmLabel: opts?.confirmLabel, resolve }),
        ),
    }),
    [show],
  );

  // Closing animates out: it keeps showing the message it was opened with (its colour, title, text
  // and buttons) instead of an empty box with the default labels.
  const [shown, setShown] = useState<Message | null>(null);
  if (message && message !== shown) setShown(message);
  const m = message ?? shown;

  const error = m?.kind === "error";
  return (
    <MessageContext.Provider value={api}>
      {children}
      <AlertDialog open={message !== null} onOpenChange={(open) => !open && close(false)}>
        <AlertDialogContent
          ref={drag.ref}
          style={drag.style}
          initialFocus={answer}
          className="max-w-[min(32rem,calc(100vw-2rem))] gap-0 overflow-hidden p-0 data-[size=default]:max-w-[min(32rem,calc(100vw-2rem))] data-[size=default]:sm:max-w-lg"
        >
          <div
            onPointerDown={drag.handle.onPointerDown}
            className={cn(
              "flex items-center gap-3 px-5 py-4 text-white",
              drag.handle.className,
              error ? "bg-red-600" : "bg-orange-500",
            )}
          >
            {error ? <OctagonXIcon className="size-7 shrink-0" /> : <TriangleAlertIcon className="size-7 shrink-0" />}
            <AlertDialogTitle className="text-lg font-semibold text-white">{m?.title}</AlertDialogTitle>
          </div>
          <div className="space-y-2 px-5 py-4">
            <AlertDialogDescription className="text-foreground text-base">{m?.body}</AlertDialogDescription>
            {m?.lines?.length ? (
              <ul className="text-muted-foreground list-disc space-y-1 pl-5 text-sm">
                {m.lines.map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            ) : null}
          </div>
          <AlertDialogFooter className="mx-0 mb-0 px-5 py-3">
            {error ? (
              <Button ref={answer} className="bg-red-600 text-white hover:bg-red-700" onClick={() => close(true)}>
                OK, I'll fix it
              </Button>
            ) : (
              <>
                <Button ref={answer} variant="outline" onClick={() => close(false)}>
                  Go back
                </Button>
                <Button className="bg-orange-500 text-white hover:bg-orange-600" onClick={() => close(true)}>
                  {m?.confirmLabel ?? "Yes, continue"}
                </Button>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MessageContext.Provider>
  );
}
